import { Bell, ChevronDown, ChevronUp, Eye, Plus } from "lucide-react";
import type React from "react";
import { useRef, useState } from "react";
import { FeatureFeedback } from "@/components/FeatureFeedback";
import { useScenarios } from "../../hooks/useScenarios";
import { useVisibleScenarios } from "../../hooks/useVisibleScenarios";
import { CreateScenarioModal } from "../modals/CreateScenarioModal";
import { PermissionGate } from "../permissions/PermissionGate";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { DependencyGraph } from "./DependencyGraph";
import { ParkedDashboard } from "./ParkedDashboard";
import { ScenarioComparison } from "./ScenarioComparison";
import { ScenarioDetailFull } from "./ScenarioDetailFull";
import { ScenarioListGallery } from "./ScenarioListGallery";

type ViewMode = "list" | "detail" | "comparison" | "parked" | "dependencies";

export const NewScenariosPage: React.FC = () => {
	const { pendingApprovalCount, pendingApprovalScenarios } = useScenarios();
	const scenarios = useVisibleScenarios();
	const containerRef = useRef<HTMLDivElement>(null);
	const [viewMode, setViewMode] = useState<ViewMode>("list");
	const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(
		null,
	);

	// Filter state lifted here so it survives navigation to detail and back
	const [filterSearchTerm, setFilterSearchTerm] = useState('');
	const [filterCategory, setFilterCategory] = useState<string>('all');
	const [filterStatus, setFilterStatus] = useState<string>('all');
	const [filterSortBy, setFilterSortBy] = useState<'recent' | 'name' | 'impact'>('recent');
	const [filterViewMode, setFilterViewMode] = useState<'grid' | 'list'>('grid');
	const [initialTab, setInitialTab] = useState<string>("builder");
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [createTemplate, setCreateTemplate] = useState<
		"growth" | "cost-cutting" | "crisis" | undefined
	>(undefined);
	const [showPendingApprovals, setShowPendingApprovals] = useState(false);
	// Scenario pre-selected for comparison (set when user clicks "Compare" on a card)
	const [compareInitialId, setCompareInitialId] = useState<string | undefined>(
		undefined,
	);

	const handleScenarioSelect = (id: string, tab?: string) => {
		setSelectedScenarioId(id);
		setInitialTab(tab || "builder");
		setViewMode("detail");
		requestAnimationFrame(() => {
			containerRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
		});
	};

	const handleEditScenario = (id: string) => {
		setSelectedScenarioId(id);
		setInitialTab("builder");
		setViewMode("detail");
		requestAnimationFrame(() => {
			containerRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
		});
	};

	const handleCompareScenario = (id: string) => {
		setCompareInitialId(id);
		setViewMode("comparison");
	};

	const handleBackToList = () => {
		setViewMode("list");
		setSelectedScenarioId(null);
		setInitialTab("builder");
	};

	const handleCreateFromTemplate = (
		template?: "growth" | "cost-cutting" | "crisis",
	) => {
		setCreateTemplate(template);
		setCreateModalOpen(true);
	};

	return (
		<div ref={containerRef} className="space-y-6 p-6">
			{viewMode === "detail" && selectedScenarioId ? (
				<ScenarioDetailFull
					scenarioId={selectedScenarioId}
					onBack={handleBackToList}
					initialTab={initialTab}
				/>
			) : (
				<>
					{/* Hero Section */}
					<div className="flex items-start justify-between">
						<div className="flex-1">
							<h1 className="text-2xl font-bold text-gray-900 mb-2">
								Scenarios
							</h1>
							<p className="text-lg text-gray-600">
								Model different business outcomes and compare their impact on
								cash flow
							</p>
						</div>

						<div className="flex items-center gap-3">
							{/* Create Button */}
							<PermissionGate permission="create_scenarios">
								<Button
									onClick={() => setCreateModalOpen(true)}
									size="lg"
									className="gap-2"
								>
									<Plus className="h-5 w-5" />
									New Scenario
								</Button>
							</PermissionGate>
						</div>
					</div>

					{/* Approvals Pending */}
					{pendingApprovalCount > 0 && (
						<div>
							<button
								type="button"
								onClick={() => setShowPendingApprovals(!showPendingApprovals)}
								className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
							>
								<Bell className="h-4 w-4 text-amber-600" />
								<span className="text-sm font-medium text-amber-900">
									{pendingApprovalCount} approval
									{pendingApprovalCount !== 1 ? "s" : ""} pending
								</span>
								{showPendingApprovals ? (
									<ChevronUp className="h-4 w-4 text-amber-600" />
								) : (
									<ChevronDown className="h-4 w-4 text-amber-600" />
								)}
							</button>

							{showPendingApprovals && (
								<div className="mt-3 space-y-2">
									{pendingApprovalScenarios.map((scenario) => (
										<div
											key={scenario.id}
											className="flex items-center justify-between bg-white border border-amber-200 rounded-lg p-3"
										>
											<div className="flex items-center gap-3">
												<div>
													<p className="font-medium text-gray-900">
														{scenario.name}
													</p>
													<p className="text-xs text-gray-500">
														Created by {scenario.createdByName}
													</p>
												</div>
												<Badge
													variant="outline"
													className="bg-amber-100 text-amber-800 border-amber-200"
												>
													{scenario.status === "pending-approval"
														? "pending approval"
														: scenario.status.replace("-", " ")}
												</Badge>
											</div>
											<Button
												variant="outline"
												size="sm"
												onClick={() => handleScenarioSelect(scenario.id)}
												className="gap-1"
											>
												<Eye className="h-4 w-4" />
												Review
											</Button>
										</div>
									))}
								</div>
							)}
						</div>
					)}

					{/* Tabs Navigation */}
					<Tabs
						value={viewMode === "detail" ? "list" : viewMode}
						onValueChange={(v) => setViewMode(v as ViewMode)}
					>
						<TabsList>
							<TabsTrigger value="list">All Scenarios</TabsTrigger>
							<PermissionGate permission="compare_scenarios">
								<TabsTrigger value="comparison">Compare</TabsTrigger>
							</PermissionGate>
							<PermissionGate permission="view_parked_scenarios">
								<TabsTrigger value="parked">Parked</TabsTrigger>
							</PermissionGate>
							<PermissionGate permission="view_scenario_dependencies">
								<TabsTrigger value="dependencies">Dependencies</TabsTrigger>
							</PermissionGate>
						</TabsList>

						<TabsContent value="list" className="mt-6">
							<ScenarioListGallery
								onSelectScenario={handleScenarioSelect}
								onEditScenario={handleEditScenario}
								onCompareScenario={handleCompareScenario}
								onCreateScenario={handleCreateFromTemplate}
								searchTerm={filterSearchTerm}
								setSearchTerm={setFilterSearchTerm}
								categoryFilter={filterCategory}
								setCategoryFilter={setFilterCategory}
								statusFilter={filterStatus}
								setStatusFilter={setFilterStatus}
								sortBy={filterSortBy}
								setSortBy={setFilterSortBy}
								viewMode={filterViewMode}
								setViewMode={setFilterViewMode}
							/>
						</TabsContent>

						<TabsContent value="comparison" className="mt-6">
							<ScenarioComparison initialScenarioId={compareInitialId} />
						</TabsContent>

						<TabsContent value="parked" className="mt-6">
							<ParkedDashboard />
						</TabsContent>

						<TabsContent value="dependencies" className="mt-6">
							<DependencyGraph />
						</TabsContent>
					</Tabs>

					<div className="flex justify-end">
						<FeatureFeedback feature="Scenarios" />
					</div>
				</>
			)}

			<CreateScenarioModal
				open={createModalOpen}
				onClose={() => {
					setCreateModalOpen(false);
					setCreateTemplate(undefined);
				}}
				initialTemplate={createTemplate}
				onScenarioCreated={(scenarioId) => {
					handleEditScenario(scenarioId);
				}}
			/>
		</div>
	);
};
